import { useEffect, useRef, useState } from 'react'
import { Check, Cloud, CloudOff, Printer } from 'lucide-react'

import { Modal } from '@/componentes/ui/Modal'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'
import { config } from '@/lib/config'
import { plataCentavos } from '@/lib/formato'
import { imprimirTicket } from '@/lib/imprimir'
import type { VentaRegistrada } from './tipos'

interface Props {
  venta: VentaRegistrada
  onReimprimir: () => void
  onCerrar: () => void
}

// Comprobante recién emitido.
//
// El ticket viene armado por el servidor como TEXTO de ancho fijo, y acá se
// muestra tal cual en una tipografía monoespaciada. Eso resuelve dos cosas de
// una: en la caja es exactamente lo que salió por la térmica (así que revisar
// un reclamo es mirar esta pantalla), y en la versión web es el comprobante
// completo listo para imprimir por el navegador, que era el requisito de que
// «la web pueda hacer todo».
//
// Imprimir en la web se hace con el diálogo del navegador y no generando un
// PDF: el resultado es el mismo, funciona en cualquier impresora que la PC ya
// tenga configurada, y no agrega una dependencia de 400 KB al bundle que baja
// por OTA.
export function DialogoTicket({ venta, onReimprimir, onCerrar }: Props) {
  // Ayuda propia y no la del POS: lo que hay adelante es el comprobante, y las
  // preguntas de acá son otras (por qué dice que no salió el papel, qué pasa
  // si no hay internet, si reimprimir genera otro número).
  useAyudaDe('pos.ticket')

  const refCerrar = useRef<HTMLButtonElement>(null)

  // El foco arranca en «Listo»: después de cobrar, lo único que el cajero
  // quiere es volver a la pantalla de venta, y con el foco puesto le alcanza
  // con Enter.
  useEffect(() => {
    refCerrar.current?.focus()
  }, [])

  const esCaja = config().runtime === 'caja'

  const [errorImpresion, setErrorImpresion] = useState<string | null>(null)

  // El ticket se imprime con el helper compartido (lib/imprimir): el mismo que
  // usa la reimpresión del backoffice, para que el papel salga igual desde las
  // dos pantallas.
  const imprimirEnNavegador = () => {
    const salio = imprimirTicket(venta.ticket, `Comprobante ${venta.numero}`)

    setErrorImpresion(
      salio
        ? null
        : 'El navegador no dejó abrir la impresión. El ticket está acá arriba: se puede '
          + 'seleccionar y copiar, o imprimir la página con Ctrl+P.',
    )
  }

  return (
    <Modal
      onCerrar={onCerrar}
      titulo={`Comprobante N° ${venta.numero}`}
      descripcion={`Total ${plataCentavos(venta.totalCentavos)}${
        venta.vueltoCentavos ? ` · vuelto ${plataCentavos(venta.vueltoCentavos)}` : ''
      }`}
      ancho="md"
    >
      <div className="space-y-4">
        {/* Estado: impreso y sincronizado. Son las dos preguntas que el cajero
            se hace después de cobrar. */}
        <div className="flex flex-wrap gap-2">
          {/* El estado de la impresora es una pregunta de la CAJA: ahí el
              papel sale solo y no salir es un problema. En el navegador se
              imprime a mano con el botón de abajo, así que un cartel de «sin
              impresora» sería un error donde no hay ninguno. */}
          {esCaja && (
            <Estado
              ok={venta.impreso}
              icono={<Printer className="h-3.5 w-3.5" />}
              si="Impreso"
              no="No salió el papel"
            />
          )}

          <Estado
            ok={!venta.pendiente}
            icono={venta.pendiente ? <CloudOff className="h-3.5 w-3.5" /> : <Cloud className="h-3.5 w-3.5" />}
            si="Sincronizado"
            no="Se sube cuando vuelva internet"
          />
        </div>

        {/* El ticket, tal cual. El fondo blanco y la mono de ancho fijo son a
            propósito: tiene que verse como el papel. */}
        <pre
          className="max-h-[46vh] overflow-auto rounded-xl border border-borde bg-papel p-4
                     text-[12.5px] leading-[1.35] text-tinta"
          style={{ fontFamily: "'Geist Mono', ui-monospace, monospace" }}
        >
          {venta.ticket}
        </pre>

        {errorImpresion && (
          <p className="rounded-lg bg-ambar/10 px-3 py-2.5 text-[13px] text-ambar">
            {errorImpresion}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {esCaja ? (
            <button type="button" onClick={onReimprimir} className="btn-secundario flex-1">
              <Printer className="h-4 w-4" /> Reimprimir
            </button>
          ) : (
            <button type="button" onClick={imprimirEnNavegador} className="btn-secundario flex-1">
              <Printer className="h-4 w-4" /> Imprimir
            </button>
          )}

          <button ref={refCerrar} type="button" onClick={onCerrar} className="btn-primario flex-[2]">
            <Check className="h-4 w-4" /> Listo, siguiente
          </button>
        </div>
      </div>
    </Modal>
  )
}

function Estado({
  ok,
  icono,
  si,
  no,
}: {
  ok: boolean
  icono: React.ReactNode
  si: string
  no: string
}) {
  return (
    <span
      className={
        'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-medium ' +
        (ok ? 'bg-verde-tenue text-verde-oscuro' : 'bg-ambar/10 text-ambar')
      }
    >
      {icono}
      {ok ? si : no}
    </span>
  )
}
